/**
 * `apps/<dir>/src/<module>.tsx` → `<dir>`.
 *
 * Every by-convention agent module the platform discovers (the face, the route
 * tree, the session entry) is globbed from the apps tree and keyed on the
 * directory name. The regex is the convention, so it lives once: a second copy
 * is a second place for a silently-wrong slug to turn an agent read-only with
 * no error anywhere.
 */
export function slugFromAppModulePath(path: string, moduleName: string): string {
  const escaped = moduleName.replace(/\./g, '\\.')
  const m = new RegExp(`/apps/([^/]+)/src/${escaped}$`).exec(path)
  if (!m) throw new Error(`module path does not match apps/<slug>/src/${moduleName}: ${path}`)
  return m[1]
}

/**
 * `apps/<dir>/src/agent.ts`'s directory name → its DECLARED `AGENT_SLUG`.
 *
 * The directory name and the slug may differ: an app directory named after its
 * surface can host an agent with another slug, and a module found under that
 * directory must be filed under the slug every real session of the agent
 * carries, or it can never be found again.
 *
 * Every discovery map below (faces, routes, session entries) runs its
 * directory key through `agentSlugForDir` before using it, so a directory
 * name that legitimately differs from its agent's slug is handled correctly
 * instead of silently going read-only.
 */
const DISCOVERED_AGENT_MODULES = import.meta.glob<{ AGENT_SLUG: string }>(
  '../../../../apps/*/src/agent.ts',
  { eager: true },
)

/** apps/<dir>/src/agent.ts's directory → its declared AGENT_SLUG. */
export const APP_DIR_TO_AGENT_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(DISCOVERED_AGENT_MODULES).map(([path, mod]) => [
    slugFromAppModulePath(path, 'agent.ts'),
    mod.AGENT_SLUG,
  ]),
)

/**
 * Resolves a discovered module's directory to the agent slug it should be
 * filed under. Throws rather than falling back to the directory name: every
 * app that ships a face/routes/sessionEntry module also ships agent.ts (the
 * app's ONLY declaration of its own slug — see agentFaces.test.ts), so a miss
 * here means that convention was violated, and guessing the directory as the
 * slug is exactly the silent-wrong-slug failure this file exists to prevent.
 */
export function agentSlugForDir(dir: string): string {
  const slug = APP_DIR_TO_AGENT_SLUG[dir]
  if (!slug) throw new Error(`apps/${dir} has no agent.ts declaring AGENT_SLUG`)
  return slug
}
